<template>
	<div class="CheckBoxSelectItem" role="group" :aria-labelledby="`checkbox-group-${item.value}`">
		<SelectItem
			:key="`${item.value}`"
			:item="parentItem"
			:type="SelectListItemType.CHECKBOX"
			:subItemsCount="allSubItemsCount"
			:partiallySelected="partiallySelected"
			@selectionChange="handleStateUpdate"
		>
			<template #icon>
				<i
					v-if="hasSubItems"
					:class="`Icon lnr-chevron-${showSubItems ? 'down' : 'up'}`"
					aria-hidden="true"
					role="button"
					tabindex="0"
					:aria-label="showSubItems ? 'Collapse options' : 'Expand options'"
					@click.stop.prevent="expandArrowFunc"
					@keydown.enter.stop.prevent="expandArrowFunc"
					@keydown.space.stop.prevent="expandArrowFunc"
				></i>
				<slot v-else name="icon">
					<i
						v-if="item.icon"
						:class="`Icon ${item.icon}`"
						aria-hidden="true"
					></i>
				</slot>
			</template>
		</SelectItem>

		<div
			v-if="hasSubItems && showSubItems"
			class="SubItems"
			role="group"
			:aria-label="`${item.text} options`"
		>
			<SelectItem
				v-if="hasSubItems"
				v-for="(subItem, index) in displayedSubItems"
				:key="`${subItem.value}-${index}`"
				:item="subItem"
				:type="SelectListItemType.CHECKBOX"
				class="SubItem"
				@selectionChange="handleSubItemStateUpdate(subItem, $event)"
			/>

			<button
				v-if="showMoreIndicator"
				type="button"
				class="ShowAll"
				:aria-expanded="showAll"
				:aria-label="showAll ? 'Show fewer options' : `Show all ${allSubItemsCount} options`"
				@click="showHideAll"
			>
				{{ showAllText }}
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import type ISelectItem from '@domain/interfaces/dtos/ISelectItem';
import SelectItem from '@components/SelectItem/SelectItem.vue';
import { SelectListItemType } from '@domain/enums/SelectListItemType';
import { SelectListItemStateType } from '@domain/enums/SelectListItemStateType';

const props = defineProps({
	item: {
		type: Object as PropType<ISelectItem>,
		required: true,
	},
	subItems: {
		type: Array as PropType<ISelectItem[]>,
		required: false,
		default: () => [],
	},
	limitSelectedSubItems: {
		type: Number,
		required: false,
		default: -1,
	}
});

const parentItem = ref<ISelectItem>({ ...props.item });
const childItems = ref<ISelectItem[]>(props.subItems ? props.subItems.map(item => ({ ...item })) : []);
const showAll = ref(false);
const expandArrow = ref(false);

const partiallySelected = computed(() => {
	if (childItems.value.length === 0) return false;

	const selectedCount = childItems.value.filter(
		(subItem: ISelectItem) => subItem.state === SelectListItemStateType.SELECTED
	).length;

	const limit = props.limitSelectedSubItems >= 0 ? props.limitSelectedSubItems : childItems.value.length;

	const isPartiallySelected = selectedCount > 0 && selectedCount < limit;
	if (isPartiallySelected && parentItem.value.state !== SelectListItemStateType.SELECTED) parentItem.value.state = SelectListItemStateType.SELECTED;

	return isPartiallySelected;
});

const emit = defineEmits<{
	'selectionChange': [state: string, item?: ISelectItem];
}>();

watch(() => props.item, (newItem) => {
	parentItem.value = { ...newItem };
}, { deep: true });

watch(() => props.subItems, (newSubItems) => {
	childItems.value = newSubItems ? newSubItems.map(item => ({ ...item })) : [];
}, { deep: true });

const handleStateUpdate = (state: string) => {
	parentItem.value.state = state as SelectListItemStateType;

	if (state !== SelectListItemStateType.SELECTED) {
		childItems.value.forEach((subItem: ISelectItem) => {
			subItem.state = SelectListItemStateType.ENABLED;
		});
		expandArrow.value = false;
	} else {
		expandArrow.value = true;
	}

	emit('selectionChange', state, parentItem.value);
};

const handleSubItemStateUpdate = (updatedSubItem: ISelectItem, state: string) => {
	const index = childItems.value.findIndex((item: ISelectItem) => item.value === updatedSubItem.value);
	if (index !== -1) {
		childItems.value[index]!.state = state as SelectListItemStateType;
	}

	const selectedCount = childItems.value.filter(
		(subItem: ISelectItem) => subItem.state === SelectListItemStateType.SELECTED
	).length;

	if (props.limitSelectedSubItems >= 0 && selectedCount >= props.limitSelectedSubItems) {
		childItems.value.forEach((subItem: ISelectItem) => {
			if (subItem.state !== SelectListItemStateType.SELECTED) {
				subItem.state = SelectListItemStateType.DISABLED;
			}
		});
	} else {
		childItems.value.forEach((subItem: ISelectItem) => {
			if (subItem.state === SelectListItemStateType.DISABLED) {
				subItem.state = SelectListItemStateType.ENABLED;
			}
		});
	}

	emit('selectionChange', state, updatedSubItem);
};

const hasSubItems = computed(() => {
	return childItems.value && childItems.value.length > 0;
});

const showSubItems = computed(() => {
	return expandArrow.value;
});

const allSubItemsCount = computed(() => {
	return props.subItems ? props.subItems.length : 0;
});

const showMoreIndicator = computed(() => {
	return allSubItemsCount.value > 7;
});

const displayedSubItems = computed(() => {
	if (!childItems.value) {
		return childItems.value;
	}

	const sortedChildItems = [...childItems.value].sort((a, b) => {
		const aSelected = a.state === SelectListItemStateType.SELECTED ? 0 : 1;
		const bSelected = b.state === SelectListItemStateType.SELECTED ? 0 : 1;
		return aSelected - bSelected;
	});

	if (showAll.value) {
		return sortedChildItems;
	}
	return sortedChildItems.slice(0, 7);
});

const showAllText = computed(() => {
	return showAll.value ? `Show less` : `Show all (${allSubItemsCount.value})`;
});

const showHideAll = () => {
	showAll.value = !showAll.value;
};

const expandArrowFunc = () => {
	expandArrow.value = !expandArrow.value;
};
</script>

<style scoped lang="scss">
.CheckBoxSelectItem {
	display: flex;
	flex-direction: column;

	@include PaddingAreas(0, 0, 1, 0);

	@include Breakpoint(Large, ExtraLarge) {
		@include PaddingAreas(0, 0, 1.5, 0);
	}

	:deep(.Icon) {
		cursor: pointer;

		&:focus-visible {
			outline: none;
		}
	}

	.SubItems {
		.SubItem {
			@include PaddingAreas(0, 0, 1, 0);
		}

		@include PaddingAreas(1.5, 0, 1, 1.5);
	}

	.ShowAll {
		cursor: pointer;
		color: $Blue-700;
		background: none;
		border: none;
		padding: 0;

		@include TextStyle(BodyMediumRegular);

		&:focus-visible {
			outline: none;
		}
	}
}
</style>
